import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useNavigationType } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLoginModal } from '../../context/LoginModalContext';
import { useActiveEditor } from '../../context/ActiveEditorContext';
import { useSettings } from '../../context/SettingsContext';
import CreatePostModal from '../CreatePostModal/CreatePostModal';
import PostCard from '../PostCard/PostCard';
import FeedContainerVoteNotice from '../FeedContainerVoteNotice/FeedContainerVoteNotice';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import PostSkeleton from '../PostSkeleton/PostSkeleton';
import RichEditor from '../RichEditor';
import ComposerPreviewModal from '../ComposerPreviewModal/ComposerPreviewModal';
import { htmlToMarkdown, shortcodesToMarkdown } from '../RichEditor/richEditorUtils';
import hiveService from '../../services/hive';
import api, { postsApi, listsApi, pollsApi } from '../../services/api';
import { useLists } from '../../context/ListsContext';
import { FEED_DISPLAY_CONFIG, getDefaultEnabledFeedKeys, getFeedCharacterLimit } from '../../config/feed-details';
import {
  buildOptimisticShortPost,
  buildOptimisticShortPostUpsertPayload
} from '../../utils/optimisticShortPost';
import './ShortFeed.css';



const SORT_OPTIONS = [
  { value: 'created',   label: 'Recent' },
  { value: 'trending',  label: 'Trending' },
  { value: 'following', label: 'Following', requiresAuth: true },
];

const ShortFeed = ({ feedType: propFeedType = 'flex', onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { feedType: paramFeedType, sort: paramSort } = useParams();
  const navigate = useNavigate();
  const navigationType = useNavigationType(); // 'POP' = browser back/forward, 'PUSH' = normal navigation
  const { user } = useAuth();
  const { signature, signatureOnShortPosts, autoload_more = true } = useSettings();
  const { openLoginModal } = useLoginModal();
  const { activeEditorId, openEditor, closeEditor } = useActiveEditor();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTab, setLoadingTab] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [timeWindow, setTimeWindow] = useState('recent');
  const [feedPaused, setFeedPaused] = useState(false);
  const [sortFallbackNotice, setSortFallbackNotice] = useState(false);
  const [currentSource, setCurrentSource] = useState('db');
  const [postContent, setPostContent] = useState('');
  const [postContentHtml, setPostContentHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [isPoll, setIsPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollChoices, setPollChoices] = useState(['', '']);
  const [pollDurationValue, setPollDurationValue] = useState(1);
  const [pollDurationUnit, setPollDurationUnit] = useState('days');
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState(paramFeedType || propFeedType);
  const [selectedSort, setSelectedSort] = useState(paramSort || 'created');
  const [enabledFeeds, setEnabledFeeds] = useState([]);
  const [showListsDropdown, setShowListsDropdown] = useState(false);
  const [listsDropdownPos, setListsDropdownPos] = useState({ top: 0, left: 0 });
  const [showMobileListsPanel, setShowMobileListsPanel] = useState(false);
  const listsDropdownRef = useRef(null);
  const listsDropdownPortalRef = useRef(null);
  const listsBtnRef = useRef(null);
  const requestIdRef = useRef(0);
  const activeLoadControllerRef = useRef(null);
  const richEditorRef = useRef(null);
  const selectedFeedRef = useRef('flex');
  const offsetRef = useRef(0);
  const currentSourceRef = useRef('db');
  const hasUserScrolledRef = useRef(false);
  const [autoloadUnlocked, setAutoloadUnlocked] = useState(false);
  
  const feedType = paramFeedType || propFeedType;
  // Use fetched feed keys to detect list feeds; fallback to empty set until the API responds.
  const knownFeedKeys = new Set(enabledFeeds.map(f => f.key));
  const isListFeed = !knownFeedKeys.has(feedType);
  const listName = isListFeed ? feedType : null;
  const { lists } = useLists();
  const listsWithMembers = lists.filter(l => l.members.length > 0);
  const activeList = isListFeed ? lists.find(l => l.name === listName) : null;
  const listFeedId = activeList?.id ?? null;
  // When viewing a list feed, fall back to the first enabled feed for create-post actions.
  const effectivePostFeed = isListFeed ? (enabledFeeds[0]?.key || 'flex') : selectedFeed;
  const createCharLimit = getFeedCharacterLimit(effectivePostFeed);
  const createRemainingChars = createCharLimit - postContent.length;
  const limit = 20;
  const editorId = `short-create:${effectivePostFeed}`;
  const showCreatePost = activeEditorId === editorId;
  const isGlobalFeed = effectivePostFeed === 'global';

  const handleCreatePostChange = (html) => {
    setPostContentHtml(html);
    const markdown = htmlToMarkdown(html);
    setPostContent(markdown);
  };

  // Fetch enabled feeds from config and enrich with FEED_DISPLAY_CONFIG
  useEffect(() => {
    const enrichFeed = (f) => ({
      ...f,
      label: FEED_DISPLAY_CONFIG[f.key]?.label || f.label,
    });

    const fetchEnabledFeeds = async () => {
      try {
        const response = await fetch('/api/feed/feeds-config');
        const feeds = await response.json();
        setEnabledFeeds(feeds.filter(f => f.enabled !== false).map(enrichFeed));
      } catch (error) {
        console.error('Error fetching feeds config:', error);
        // Fallback using FEED_DISPLAY_CONFIG defaults
        setEnabledFeeds(
          getDefaultEnabledFeedKeys().map(key => ({
            key,
            label: FEED_DISPLAY_CONFIG[key]?.label || key,
          }))
        );
      }
    };
    
    fetchEnabledFeeds();
  }, []);

  useEffect(() => {
    setSelectedFeed(feedType);
  }, [feedType]);

  useEffect(() => {
    setSelectedSort(paramSort || 'created');
  }, [paramSort]);

  useEffect(() => {
    selectedFeedRef.current = selectedFeed;
  }, [selectedFeed]);

  useEffect(() => {
    hasUserScrolledRef.current = false;
    setAutoloadUnlocked(false);
  }, [selectedFeed, selectedSort]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    currentSourceRef.current = currentSource;
  }, [currentSource]);

  useEffect(() => {
    return () => {
      try {
        activeLoadControllerRef.current?.abort();
      } catch (err) {
        // ignore cleanup failures
      }
    };
  }, []);

  useEffect(() => {
    if (!showListsDropdown) return;
    const handleClickOutside = (e) => {
      if (
        listsDropdownRef.current && !listsDropdownRef.current.contains(e.target) &&
        (!listsDropdownPortalRef.current || !listsDropdownPortalRef.current.contains(e.target))
      ) {
        setShowListsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showListsDropdown]);

  const loadPosts = useCallback(async (reset = false, source = currentSourceRef.current, feedOverride = null, overrideTimeWindow = null) => {
    // In list mode, wait until the list ID has been resolved from context.
    if (isListFeed && !listFeedId) return;

    const feed = feedOverride || selectedFeedRef.current || feedType;
    const requestId = ++requestIdRef.current;

    try {
      activeLoadControllerRef.current?.abort();
    } catch (err) {
      // ignore abort failures
    }

    let controller = null;
    if (!isListFeed) {
      controller = new AbortController();
      activeLoadControllerRef.current = controller;
    }

    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
        offsetRef.current = 0;
        setNewPostsCount(0);
      } else {
        setLoadingMore(true);
      }

      const currentOffset = reset ? 0 : offsetRef.current;
      const currentTimeWindow = overrideTimeWindow || timeWindow;

      let data;
      if (isListFeed) {
        data = await listsApi.getListFeed(listFeedId, limit, currentOffset);
      } else {
        const params = { limit, offset: currentOffset, source, sort: selectedSort, timeWindow: currentTimeWindow };
        if (selectedSort === 'following' && user?.username) {
          params.observer = user.username;
        }
        const response = await api.get(`/feed/${feed}`, { params, signal: controller.signal });
        data = response.data;
      }

      const normalizedPosts = (data.posts || []).map((post) => ({
        ...post,
        created: post.created || post.created_at || null
      }));

      // Ignore stale responses from previous feed/source requests.
      if (requestId !== requestIdRef.current) {
        return;
      }

      if (reset) {
        setPosts(normalizedPosts);
        setCurrentSource(source);
      } else {
        setPosts(prev => [...prev, ...normalizedPosts]);
      }

      setHasMore(data.hasMore || false);

      if (!isListFeed && reset) {
        const pauseTrendingOrHot =
          selectedSort === 'trending' &&
          currentTimeWindow === 'recent' &&
          !data.hasMore;
        setFeedPaused(pauseTrendingOrHot);

        const fallbackToCreated =
          selectedSort === 'trending' &&
          currentTimeWindow === 'older' &&
          data.sortApplied === 'created';
        setSortFallbackNotice(fallbackToCreated);
      }

      const nextOffset = currentOffset + limit;
      setOffset(nextOffset);
      offsetRef.current = nextOffset;
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Error loading posts:', error);
    } finally {
      // Only latest request should clear loading states.
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
        setLoadingTab(false);
        setLoadingMore(false);
      }
    }
  }, [feedType, limit, isListFeed, listFeedId, selectedSort, timeWindow, user?.username]);

  useEffect(() => {
    setTimeWindow('recent');
    setFeedPaused(false);
    setSortFallbackNotice(false);
    loadPosts(true, 'auto', null, 'recent');
    setNewPostsCount(0);
  }, [selectedFeed, selectedSort, user, loadPosts]);

  const handleLoadOlderPosts = () => {
    setFeedPaused(false);
    setSortFallbackNotice(false);
    setTimeWindow('older');
    setOffset(0);
    offsetRef.current = 0;
    loadPosts(true, 'auto', null, 'older');
  };

  // If returning from a detail view via browser back or explicit back-link, scroll to the post that was active.
  // Do NOT activate when the user navigates to the feed normally (PUSH/REPLACE).
  useEffect(() => {
    if (!posts || posts.length === 0) return;
    // Only restore focus on POP navigation (browser back) or if an explicit back-link set preserveScroll
    const isBackNavigation = navigationType === 'POP' || sessionStorage.getItem('preserveScroll') === 'true';
    if (!isBackNavigation) {
      // Normal navigation — clear any stale focusPost so it doesn't activate later
      sessionStorage.removeItem('focusPost');
      sessionStorage.removeItem('preserveScroll');
      return;
    }
    const focusKey = sessionStorage.getItem('focusPost');
    if (!focusKey) return;

    let cancelled = false;

    const tryFocus = () => {
      const el = document.querySelector(`[data-postid="${focusKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'center' });
        el.classList.add('focus-highlight');
        setTimeout(() => el.classList.remove('focus-highlight'), 1800);
        sessionStorage.removeItem('focusPost');
        return true;
      }
      return false;
    };

    // If post present in current list, focus immediately
    if (tryFocus()) return;

    // Retry loading more pages until found (safety bound)
    (async () => {
      const MAX_PAGES = 6; // try up to ~6 pages (configurable)
      let pagesTried = 0;

      while (!cancelled && pagesTried < MAX_PAGES && hasMore) {
        pagesTried += 1;
        try {
          await loadPosts(false, currentSourceRef.current);
          // allow React to render appended posts before querying DOM
          await new Promise((r) => setTimeout(r, 150));
          if (tryFocus()) return;
          // loop continues if not found and hasMore still true
        } catch (err) {
          // stop retrying on fetch error
          break;
        }
      }

      // If we exit loop without finding the post, clear the session key to avoid repeated attempts
      if (!cancelled) {
        sessionStorage.removeItem('focusPost');
      }
    })();

    return () => { cancelled = true; };
  }, [posts, hasMore, loadPosts, navigationType]);

  const handleRefresh = () => {
    hasUserScrolledRef.current = false;
    setAutoloadUnlocked(false);
    setTimeWindow('recent');
    setFeedPaused(false);
    setSortFallbackNotice(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setRefreshing(true);
    setNewPostsCount(0);
    loadPosts(true, 'live', null, 'recent').catch(() => {
      // ignore refresh errors
    });
  };

  const handleSortChange = (newSort) => {
    if (newSort === 'following' && !user) return;
    setPosts([]);
    setHasMore(true);
    setTimeWindow('recent');
    setFeedPaused(false);
    setSortFallbackNotice(false);
    setOffset(0);
    offsetRef.current = 0;
    setSelectedSort(newSort);
    setLoadingTab(true);
    window.scrollTo(0, 0);
    navigate(`/feed/${selectedFeed}/${newSort}`);
  };

  const handleFeedChange = (newFeed) => {
    // Clear focusPost so we don't try to focus a post from a previous feed
    sessionStorage.removeItem('focusPost');
    setPosts([]);
    setHasMore(true);
    setTimeWindow('recent');
    setFeedPaused(false);
    setSortFallbackNotice(false);
    setOffset(0);
    offsetRef.current = 0;
    setSelectedFeed(newFeed);
    setLoadingTab(true);
    window.scrollTo(0, 0);
    navigate(`/feed/${newFeed}/${selectedSort}`);
  };

  const handleLoadMore = useCallback((trigger = 'manual') => {
    if (trigger === 'manual') {
      setAutoloadUnlocked(true);
    }
    if (!loadingMore && !loading && hasMore && !feedPaused) {
      loadPosts(false, currentSourceRef.current);
    }
  }, [loadingMore, loading, hasMore, feedPaused, loadPosts]);

  const loadMoreObserverRef = useRef(null);

  useEffect(() => {
    if (!autoload_more) return;

    const checkUnlock = () => {
      if (window.scrollY > 0) {
        hasUserScrolledRef.current = true;
      }

      const doc = document.documentElement;
      const nearBottom = (window.innerHeight + window.scrollY) >= (doc.scrollHeight - 64);

      if (hasUserScrolledRef.current && nearBottom) {
        setAutoloadUnlocked(true);
      }
    };

    window.addEventListener('scroll', checkUnlock, { passive: true });
    return () => {
      window.removeEventListener('scroll', checkUnlock);
    };
  }, [autoload_more]);

  useEffect(() => {
    if (!autoload_more || !autoloadUnlocked || !hasMore || loading || loadingMore || feedPaused) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore('observer');
        }
      },
      { rootMargin: '120px' }
    );

    const target = loadMoreObserverRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [autoload_more, autoloadUnlocked, hasMore, loading, loadingMore, feedPaused, handleLoadMore]);

  useEffect(() => {
    const newestLoadedMs = posts.reduce((maxMs, post) => {
      const raw = post?.created || post?.created_at;
      if (!raw) return maxMs;
      const parsedMs = new Date(raw).getTime();
      if (Number.isNaN(parsedMs)) return maxMs;
      return Math.max(maxMs, parsedMs);
    }, 0);

    if (!selectedFeed || isListFeed || newestLoadedMs <= 0) return;

    const latestPostDate = new Date(newestLoadedMs).toISOString();

    let isActive = true;

    const checkForNewPosts = async () => {
      try {
        const feed = selectedFeed || feedType;
        const accountQuery = (feed === 'home' || feed === 'following') && user?.username
          ? `account=${encodeURIComponent(user.username)}&`
          : '';
        const response = await api.get(
          `/feed/check-new?${accountQuery}feedType=${feed}&sort=${encodeURIComponent(selectedSort)}&timeWindow=${encodeURIComponent(timeWindow)}&lastCreatedAt=${encodeURIComponent(latestPostDate)}`
        );
        const data = response.data;

        if (isActive) {
          const count = Number(data?.count || 0);
          setNewPostsCount(Number.isFinite(count) && count > 0 ? count : 0);
        }
      } catch (error) {
        // ignore polling errors
      }
    };

    checkForNewPosts();
    const intervalId = setInterval(checkForNewPosts, 10000);

    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [selectedFeed, posts, feedType, isListFeed, selectedSort, timeWindow, user?.username]);

  const updateOptimisticPost = (permlink, updates) => {
    setPosts(prev => prev.map(post => post.permlink === permlink ? { ...post, ...updates } : post));
  };

  const schedulePostConfirmationCheck = (author, permlink) => {
    const checkDelays = [20000, 60000];

    checkDelays.forEach((delay, index) => {
      setTimeout(async () => {
        const exists = await hiveService.checkPostExists(author, permlink);
        if (exists) {
          updateOptimisticPost(permlink, { optimistic: false, optimisticStatus: 'confirmed' });
          return;
        }

        if (index === checkDelays.length - 1) {
          updateOptimisticPost(permlink, { optimisticStatus: 'failed', optimisticError: 'Not found on-chain yet. Retry?' });
        }
      }, delay);
    });
  };

  const handleRetryOptimisticPost = async (post) => {
    try {
      updateOptimisticPost(post.permlink, { optimisticStatus: 'pending', optimisticError: '' });
      let jsonMetadata = post.json_metadata;
      if (typeof jsonMetadata === 'string') {
        try {
          jsonMetadata = JSON.parse(jsonMetadata);
        } catch (err) {
          // ignore parse errors
        }
      }

      await hiveService.createPost(
        post.author,
        post.body,
        post.optimisticTags || [effectivePostFeed],
        post.optimisticFeedType || effectivePostFeed,
        { 
          permlink: post.permlink,
          targetPost: post.parentAuthor && post.parentPermlink ? { author: post.parentAuthor, permlink: post.parentPermlink } : undefined,
          jsonMetadata
        }
      );
      // ensure the post is persisted now that the broadcast succeeded
      await postsApi.upsertOptimisticShortPost(buildOptimisticShortPostUpsertPayload({
        author: post.author,
        permlink: post.permlink,
        body: post.body,
        createdAt: post.created,
        jsonMetadata,
        targetPost: post.parentAuthor && post.parentPermlink
          ? { author: post.parentAuthor, permlink: post.parentPermlink }
          : undefined,
        voteCount: post.votes?.total || 0,
        replyCount: post.replies || 0
      }));
      schedulePostConfirmationCheck(post.author, post.permlink);
    } catch (error) {
      updateOptimisticPost(post.permlink, { optimisticStatus: 'failed', optimisticError: error.message || 'Retry failed' });
    }
  };

  const handleCreatePost = async () => {
    if (!user) {
      alert('Please login to post');
      return;
    }

    if (!postContent.trim()) {
      return;
    }

    if (postContent.length > createCharLimit) {
      alert(`Post exceeds ${createCharLimit} character limit for ${effectivePostFeed}`);
      return;
    }

    setPosting(true);

    const permlink = effectivePostFeed === 'threads'
      ? hiveService.generateThreadsPermlink('leothreads')
      : hiveService.generatePermlink();
    const createdAt = new Date().toISOString();

    // Convert shortcodes to standard markdown just before broadcast so other
    // Hive clients render correctly. Flex/Flexus are ShortApp-only and keep shortcodes.
    const isFlex = effectivePostFeed === 'flex' || effectivePostFeed === 'flexus';
    const broadcastBody = isFlex ? postContent : shortcodesToMarkdown(postContent);
    const bodyWithSignature = hiveService.appendUserSignatureForContext(
      broadcastBody,
      user.username,
      'short',
      signature,
      signatureOnShortPosts
    );

    const bodyTags = Array.from(new Set((broadcastBody.match(/#[a-z0-9-]+/gi) || []).map((h) => h.slice(1).toLowerCase())));
    const allTags = Array.from(new Set([effectivePostFeed, ...bodyTags]));

    let pollMetadata = null;
    let pollEndTimeIso = null;

    if (isPoll) {
      const trimmedQuestion = pollQuestion.trim();
      const validChoices = pollChoices.map(c => c.trim()).filter(Boolean);

      if (!trimmedQuestion) {
        alert('Please enter a poll question');
        return;
      }
      if (validChoices.length < 2) {
        alert('Please provide at least 2 poll choices');
        return;
      }

      const numVal = Math.max(1, Number(pollDurationValue) || 1);
      const maxMinutes = 7 * 24 * 60; // 10080 minutes (7 days)
      let durationMinutes = numVal;
      if (pollDurationUnit === 'days') {
        durationMinutes = numVal * 24 * 60;
      } else if (pollDurationUnit === 'hours') {
        durationMinutes = numVal * 60;
      }
      if (durationMinutes > maxMinutes) {
        durationMinutes = maxMinutes;
      }

      const endTimestampSec = Math.floor(Date.now() / 1000) + (durationMinutes * 60);
      pollEndTimeIso = new Date(endTimestampSec * 1000).toISOString();

      pollMetadata = {
        content_type: 'poll',
        version: 0.8,
        question: trimmedQuestion,
        choices: validChoices,
        preferred_interpretation: 'number_of_votes',
        end_time: endTimestampSec,
        allow_vote_changes: true,
        filters: { account_age: 0 },
        ui_hide_res_until_voted: false
      };
    }

    try {
      const payload = await hiveService.buildShortPostPayload(
        user.username,
        bodyWithSignature,
        allTags,
        effectivePostFeed,
        permlink
      );

      const mergedJsonMetadata = {
        ...(payload.jsonMetadata || {}),
        ...(pollMetadata || {})
      };

      const optimisticPost = buildOptimisticShortPost({
        author: user.username,
        permlink,
        body: bodyWithSignature,
        createdAt,
        jsonMetadata: mergedJsonMetadata,
        targetPost: payload.targetPost,
        feedType: effectivePostFeed,
        tags: allTags
      });

      setPosts(prev => [optimisticPost, ...prev]);

      try {
        await hiveService.createPost(
          user.username,
          bodyWithSignature,
          allTags,
          effectivePostFeed,
          {
            permlink,
            targetPost: payload.targetPost,
            jsonMetadata: mergedJsonMetadata,
            signature,
            applySignature: signatureOnShortPosts,
          }
        );

        if (pollMetadata) {
          try {
            await pollsApi.upsertPoll(user.username, permlink, {
              question: pollMetadata.question,
              choices: pollMetadata.choices,
              preferred_interpretation: pollMetadata.preferred_interpretation,
              end_time: pollEndTimeIso,
              allow_vote_changes: pollMetadata.allow_vote_changes,
              hide_res_until_voted: pollMetadata.ui_hide_res_until_voted,
              filters: pollMetadata.filters
            });
          } catch (pollDbErr) {
            console.warn('Failed to register poll in local database:', pollDbErr);
          }
        }

        await postsApi.upsertOptimisticShortPost(buildOptimisticShortPostUpsertPayload({
          author: user.username,
          permlink,
          body: bodyWithSignature,
          createdAt,
          jsonMetadata: mergedJsonMetadata,
          targetPost: payload.targetPost
        }));

        setPostContent('');
        setPostContentHtml('');
        setIsPoll(false);
        setPollQuestion('');
        setPollChoices(['', '']);
        setPreviewOpen(false);
        closeEditor(editorId);
        schedulePostConfirmationCheck(user.username, permlink);
      } catch (error) {
        updateOptimisticPost(permlink, {
          optimisticStatus: 'pending',
          optimisticError: error.message || 'Broadcast returned an error, verifying on-chain...'
        });
        console.error('Error creating post:', error);
        // Keychain sometimes reports an internal error in new-tab contexts even though
        // the transaction was broadcast successfully.  Schedule a confirmation check so
        // the optimistic post auto-confirms if it actually made it on-chain.
        schedulePostConfirmationCheck(user.username, permlink);
      }
    } catch (error) {
      console.error('Error creating post:', error);
      updateOptimisticPost(permlink, { optimisticStatus: 'failed', optimisticError: error.message || 'Failed to post' });
      alert(`Failed to create post: ${error.message || 'Please try again.'}`);
    } finally {
      setPosting(false);
    }
  };

  const getCreateButtonLabel = () => {
    if (effectivePostFeed === 'global') return 'Create Post';
    const cfg = FEED_DISPLAY_CONFIG[effectivePostFeed];
    return cfg ? `Create ${cfg.inlineLabel}` : 'Create';
  };

  const getInlineCreateButtonLabel = () => {
    if (effectivePostFeed === 'global') return 'Post';
    const cfg = FEED_DISPLAY_CONFIG[effectivePostFeed];
    return cfg?.postLabel || 'Post';
  };

  const getFeedTitle = () => {
    if (isListFeed) return listName || 'List';
    if (selectedFeed === 'global') return 'Global';
    if (selectedFeed === 'trending') return 'Trending';
    return FEED_DISPLAY_CONFIG[selectedFeed]?.label || 'Feed';
  };

  const feedTitle = getFeedTitle();

  const getPreviewParentAuthor = () => {
    if (selectedFeed === 'flexus') return FEED_DISPLAY_CONFIG['flex']?.root_author || 'hive-144726';
    return FEED_DISPLAY_CONFIG[selectedFeed]?.root_author || 'hive-144726';
  };

  // Mirror actual publish flow so preview matches what is broadcast.
  const isPreviewFlex = selectedFeed === 'flex' || selectedFeed === 'flexus';
  const previewBroadcastBody = isPreviewFlex ? postContent : shortcodesToMarkdown(postContent);
  const previewBodyWithSignature = user
    ? hiveService.appendUserSignatureForContext(previewBroadcastBody, user.username, 'short', signature, signatureOnShortPosts)
    : previewBroadcastBody;

  const previewPost = user ? {
    author: user.username,
    permlink: 'preview-short',
    body: previewBodyWithSignature,
    created: new Date().toISOString(),
    json_metadata: JSON.stringify({ app: 'flexus' }),
    parentAuthor: getPreviewParentAuthor(),
    parentPermlink: selectedFeed || 'flex',
    depth: 1,
    votes: { total: 0, up: 0 },
    replies: 0,
  } : null;

  if (loading && posts.length === 0) {
    return (
      <div className="short-feed">
        <div className="feed-loading">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="short-feed">
      {/* Feed Stick - Sticky wrapper for header, tabs and create post */}
      <div className="feed-stick">
        {/* Feed Header */}
        <div className="feed-header">
          <button 
            className="menu-open-btn mobile-only"
            onClick={onOpenLeftSidebar}
            aria-label="Open menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h2 className="feed-title desktop-only">{feedTitle}</h2>
          <select 
            className="feed-dropdown mobile-only"
            value={selectedFeed}
            onChange={(e) => {
              if (e.target.value === '__lists__') {
                setShowMobileListsPanel(true);
              } else {
                handleFeedChange(e.target.value);
              }
            }}
          >
            {isListFeed && (
              <option value={selectedFeed}>{listName}</option>
            )}
            {enabledFeeds.map((feed) => (
              <option key={feed.key} value={feed.key}>
                {feed.label}
              </option>
            ))}
            {user && listsWithMembers.length > 0 && (
              <option value="__lists__">Lists ▾</option>
            )}
          </select>
          <select
            className="feed-sort-dropdown mobile-only"
            value={selectedSort}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            {SORT_OPTIONS.map(opt => {
              if (opt.requiresAuth && !user) return null;
              return (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              );
            })}
          </select>
          <button 
            className={`refresh-btn ${newPostsCount > 0 ? 'has-new' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <span className="refresh-text">Refresh</span>
            {newPostsCount > 0 && (
              <span className="refresh-count-badge">{newPostsCount}</span>
            )}
            <svg 
              className={`refresh-icon ${refreshing ? 'spinning' : ''}`}
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
          <button 
            className="menu-open-btn mobile-only"
            onClick={onOpenRightSidebar}
            aria-label="Open account info"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </button>
        </div>

        {/* Feed Tabs */}
        <div className="feed-tabs desktop-only">
          {enabledFeeds.map((feed) => (
            <button
              key={feed.key}
              className={`feed-tab ${selectedFeed === feed.key ? 'active' : ''}`}
              onClick={() => handleFeedChange(feed.key)}
              disabled={loadingTab}
            >
              {loadingTab && selectedFeed === feed.key ? 'Loading...' : feed.label}
            </button>
          ))}
          {isListFeed && (
            <button
              className="feed-tab active"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setListsDropdownPos({ top: rect.bottom + 2, left: rect.left });
                setShowListsDropdown(v => !v);
              }}
            >
              {listName} ▾
            </button>
          )}
          {user && listsWithMembers.length > 0 && (
            <div className="feed-tab-lists-wrapper" ref={listsDropdownRef}>
              <button
                ref={listsBtnRef}
                className={`feed-tab feed-tab-lists${isListFeed ? ' active' : ''}`}
                onClick={() => {
                  const rect = listsBtnRef.current?.getBoundingClientRect();
                  if (rect) setListsDropdownPos({ top: rect.bottom + 2, left: rect.left });
                  setShowListsDropdown(v => !v);
                }}
                aria-haspopup="listbox"
                aria-expanded={showListsDropdown}
              >
                Lists ▾
              </button>
              {showListsDropdown && ReactDOM.createPortal(
                <div
                  ref={listsDropdownPortalRef}
                  className="feed-tab-lists-dropdown"
                  role="listbox"
                  style={{ top: listsDropdownPos.top, left: listsDropdownPos.left }}
                >
                  {listsWithMembers.map(list => (
                    <button
                      key={list.id}
                      className="feed-tab-lists-item"
                      role="option"
                      aria-selected={false}
                      onClick={() => {
                        setShowListsDropdown(false);
                        handleFeedChange(encodeURIComponent(list.name));
                      }}
                    >
                      <span className="lists-item-name">{list.name}</span>
                      <span className="lists-item-count">{list.members.length}</span>
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </div>
          )}
        </div>

        {/* Sort Tabs for Desktop */}
        <div className="feed-sort-tabs desktop-only">
          {SORT_OPTIONS.map(opt => {
            if (opt.requiresAuth && !user) return null;
            return (
              <button
                key={opt.value}
                className={`feed-sort-tab ${selectedSort === opt.value ? 'active' : ''}`}
                onClick={() => handleSortChange(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Create Post Section */}
        <div className="feed-create-section">
          {user ? (
            <>
              {isGlobalFeed ? (
                <button 
                  className="create-post-btn"
                  onClick={() => setShowCreatePostModal(true)}
                >
                  {getCreateButtonLabel()}
                </button>
              ) : !showCreatePost ? (
                <button 
                  className="create-post-btn"
                  onClick={() => openEditor(editorId)}
                >
                  {getCreateButtonLabel()}
                </button>
              ) : (
                <div className="create-post-inline">
                  <RichEditor
                    ref={richEditorRef}
                    value={postContentHtml}
                    onChange={handleCreatePostChange}
                    onSubmit={handleCreatePost}
                    placeholder={`What's on your mind?`}
                    maxLength={null}
                    toolbar={true}
                  />
                  <div className="create-post-actions">
                    <div className="composer-meta-actions">
                      <span className="char-count">{createRemainingChars} remaining</span>
                      <button
                        className="preview-create-btn"
                        onClick={() => setPreviewOpen(true)}
                        disabled={!postContent.trim() || posting}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        className={`poll-toggle-btn ${isPoll ? 'active' : ''}`}
                        onClick={() => setIsPoll(!isPoll)}
                      >
                        <span>📊</span>
                        <span>{isPoll ? 'Remove Poll' : 'Create Poll'}</span>
                      </button>
                    </div>
                    <div className="action-buttons">
                      <button 
                        className="cancel-create-btn"
                        onClick={() => {
                          setPreviewOpen(false);
                          closeEditor(editorId);
                          setPostContent('');
                          setPostContentHtml('');
                        }}
                        disabled={posting}
                      >
                        Cancel
                      </button>
                      <button 
                        className="post-create-btn"
                        onClick={handleCreatePost}
                        disabled={!postContent.trim() || posting || createRemainingChars < 0}
                      >
                        {posting ? 'Posting...' : getInlineCreateButtonLabel()}
                      </button>
                    </div>
                  </div>

                  {isPoll && (
                    <div className="poll-creator-box">
                      <h4 className="poll-creator-title">Poll Setup</h4>
                      <input
                        type="text"
                        className="poll-input-field"
                        placeholder="Ask a question..."
                        value={pollQuestion}
                        onChange={(e) => setPollQuestion(e.target.value)}
                      />

                      <div className="poll-choices-container">
                        {pollChoices.map((choice, idx) => (
                          <div key={idx} className="poll-choice-row">
                            <input
                              type="text"
                              className="poll-input-field"
                              placeholder={`Choice ${idx + 1}`}
                              value={choice}
                              onChange={(e) => {
                                const updated = [...pollChoices];
                                updated[idx] = e.target.value;
                                setPollChoices(updated);
                              }}
                            />
                            {pollChoices.length > 2 && (
                              <button
                                type="button"
                                className="poll-remove-choice-btn"
                                title="Remove choice"
                                onClick={() => {
                                  setPollChoices(pollChoices.filter((_, i) => i !== idx));
                                }}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}

                        {pollChoices.length < 10 && (
                          <button
                            type="button"
                            className="poll-add-choice-btn"
                            onClick={() => setPollChoices([...pollChoices, ''])}
                          >
                            + Add Option
                          </button>
                        )}
                      </div>

                      <div className="poll-duration-row">
                        <span>Poll duration:</span>
                        <input
                          type="number"
                          min="1"
                          max={pollDurationUnit === 'days' ? 7 : (pollDurationUnit === 'hours' ? 168 : 10080)}
                          value={pollDurationValue}
                          onChange={(e) => {
                            const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                            const max = pollDurationUnit === 'days' ? 7 : (pollDurationUnit === 'hours' ? 168 : 10080);
                            setPollDurationValue(Math.min(val, max));
                          }}
                          className="poll-duration-input"
                        />
                        <select
                          value={pollDurationUnit}
                          onChange={(e) => {
                            const newUnit = e.target.value;
                            setPollDurationUnit(newUnit);
                            const max = newUnit === 'days' ? 7 : (newUnit === 'hours' ? 168 : 10080);
                            if (pollDurationValue > max) {
                              setPollDurationValue(max);
                            }
                          }}
                          className="poll-duration-select"
                        >
                          <option value="days">day(s)</option>
                          <option value="hours">hour(s)</option>
                          <option value="minutes">minute(s)</option>
                        </select>
                        <span className="poll-duration-hint">(max 7 days)</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <button 
              className="create-post-btn login-prompt-btn"
              onClick={openLoginModal}
            >
              Login to Post
            </button>
          )}

          <FeedContainerVoteNotice feedType={selectedFeed} onVoted={handleRefresh} isFeedReady={!loading && !loadingTab} />
        </div>

        <ComposerPreviewModal
          isOpen={previewOpen}
          title="Preview Short"
          content={postContent}
          previewNode={previewPost ? <PostCard post={previewPost} showActions={false} showCommentToggle={false} hideCommentButton={true} /> : null}
          author={'you'}
          focusTargetRef={richEditorRef}
          submitLabel={getInlineCreateButtonLabel()}
          isSubmitting={posting}
          onClose={() => {
            setPreviewOpen(false);
            window.setTimeout(() => {
              richEditorRef.current?.focus?.();
            }, 0);
          }}
          onKeepWriting={() => {
            setPreviewOpen(false);
            window.setTimeout(() => {
              richEditorRef.current?.focus?.();
            }, 0);
          }}
          onSubmit={handleCreatePost}
        />
      </div>

      {/* Posts List */}
      <div className="feed-posts" style={{ position: 'relative' }}>
        {newPostsCount > 0 && (
          <button
            className="new-posts-fab"
            onClick={handleRefresh}
          >
            ↑ {newPostsCount} New Post{newPostsCount > 1 ? 's' : ''}
          </button>
        )}
        {loadingTab ? (
          <div className="feed-loading">
            <LoadingSpinner />
          </div>
        ) : posts.length === 0 ? (
          <div className="feed-empty">
            <p>No posts yet. {user && feedType === 'home' && 'Follow some users to see their posts here!'}</p>
          </div>
        ) : (
          posts.map((post) => (
            <PostCard 
              key={`${post.author}-${post.permlink}`}
              post={post}
              showActions={true}
              onRetryOptimistic={handleRetryOptimisticPost}
            />
          ))
        )}
      </div>

      {feedPaused && (
        <div className="feed-pause-notice" style={{ padding: '1rem 1.25rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', margin: '0.75rem 0 0' }}>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            You&apos;ve seen all posts from the last 7 days for this sort.
          </p>
          <button className="secondary-btn" onClick={handleLoadOlderPosts}>
            Load older posts
          </button>
        </div>
      )}

      {sortFallbackNotice && (
        <div className="feed-pause-notice" style={{ padding: '0.85rem 1rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', margin: '0.75rem 0 0' }}>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Older posts are currently sorted by newest first.
          </p>
        </div>
      )}

      {/* Load More */}
      {hasMore && !feedPaused && posts.length > 0 && (
        <div className="feed-load-more" ref={autoload_more ? loadMoreObserverRef : null}>
          {loadingMore ? (
            <>
              <PostSkeleton />
              <PostSkeleton />
            </>
          ) : (
            <button 
              className="load-more-btn"
              onClick={() => handleLoadMore('manual')}
              disabled={loading || loadingMore}
            >
              Load More
            </button>
          )}
        </div>
      )}

      {showCreatePostModal && isGlobalFeed && (
        <CreatePostModal onClose={() => setShowCreatePostModal(false)} />
      )}

      {/* Mobile Lists Panel */}
      {showMobileListsPanel && ReactDOM.createPortal(
        <>
          <div className="mobile-lists-backdrop" onClick={() => setShowMobileListsPanel(false)} />
          <div className="mobile-lists-panel">
            <div className="mobile-lists-panel-header">
              <span className="mobile-lists-panel-title">Lists</span>
              <button
                className="mobile-lists-panel-close"
                onClick={() => setShowMobileListsPanel(false)}
                aria-label="Close lists"
              >×</button>
            </div>
            <ul className="mobile-lists-panel-list">
              {listsWithMembers.map(list => (
                <li key={list.id}>
                  <button
                    className="mobile-lists-panel-item"
                    onClick={() => {
                      setShowMobileListsPanel(false);
                      navigate(`/feed/${encodeURIComponent(list.name)}`);
                    }}
                  >
                    <span className="mobile-lists-panel-name">{list.name}</span>
                    <span className="mobile-lists-panel-count">{list.members.length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>,
        document.body
      )}

    </div>
  );
};

export default ShortFeed;
